// Pure helpers that turn a downloaded game zip into files the offline service
// worker (public/sw.js) can serve. Kept free of browser APIs so they're unit tested.

// Must match public/sw.js.
export const GAMES_CACHE = "akedo-offline-games-v1";
export const OFFLINE_GAMES_PATH = "/offline-games/";
const AKEDO_FILES_DIR = "__akedo__";

// Encodes a path segment the way browsers do in URLs, so the cache key matches
// the request (e.g. "+" and "(" stay as they are, a space becomes %20).
const encodeSegment = (segment) =>
	encodeURIComponent(segment).replace(/%(21|24|26|27|28|29|2A|2B|2C|3A|3B|3D|40)/gi, (match) => decodeURIComponent(match));

export const gameFileUrl = (appId, relativePath) =>
	`${OFFLINE_GAMES_PATH}${encodeSegment(appId)}/${relativePath.split("/").map(encodeSegment).join("/")}`;

export const akedoFileUrl = (appId, fileName) => gameFileUrl(appId, `${AKEDO_FILES_DIR}/${fileName}`);

const CONTENT_TYPES = {
	html: "text/html; charset=utf-8",
	htm: "text/html; charset=utf-8",
	js: "application/javascript",
	mjs: "application/javascript",
	css: "text/css",
	json: "application/json",
	txt: "text/plain; charset=utf-8",
	xml: "application/xml",
	svg: "image/svg+xml",
	png: "image/png",
	jpg: "image/jpeg",
	jpeg: "image/jpeg",
	gif: "image/gif",
	webp: "image/webp",
	ico: "image/x-icon",
	wasm: "application/wasm",
	mp3: "audio/mpeg",
	ogg: "audio/ogg",
	wav: "audio/wav",
	m4a: "audio/mp4",
	aac: "audio/aac",
	webm: "audio/webm",
	mp4: "video/mp4",
	woff: "font/woff",
	woff2: "font/woff2",
	ttf: "font/ttf",
	otf: "font/otf",
};

const extensionOf = (path) => {
	const match = /\.([a-z0-9]+)$/i.exec(path);
	return match ? match[1].toLowerCase() : "";
};

// Unity serves "Build.data.gz" with Content-Encoding: gzip, so the browser hands
// the game decompressed bytes. A service worker can't set Content-Encoding, so
// those files are stored decompressed and served under the original name.
export const isGzipFile = (path) => extensionOf(path) === "gz";
export const isBrotliFile = (path) => extensionOf(path) === "br";
const servedPath = (path) => (isGzipFile(path) ? path.replace(/\.gz$/i, "") : path);

export const contentTypeFor = (path) => CONTENT_TYPES[extensionOf(servedPath(path))] || "application/octet-stream";

const TEXT_EXTENSIONS = new Set(["html", "htm", "js", "mjs", "json", "css"]);
export const isPatchableText = (path) => TEXT_EXTENSIONS.has(extensionOf(path));

const isJunk = (path) => path.startsWith("__MACOSX/") || /(^|\/)\.DS_Store$/.test(path);

// The folder holding the game's index.html (zips may wrap the game in a folder).
// Returns null when there's no index.html.
export const findGameRoot = (paths) => {
	const indexes = paths.filter((path) => !isJunk(path) && /(^|\/)index\.html$/i.test(path));
	if (!indexes.length) return null;
	const shallowest = indexes.sort((a, b) => a.split("/").length - b.split("/").length)[0];
	return shallowest.slice(0, shallowest.length - "index.html".length);
};

// Path inside the game folder, or null for files that shouldn't be stored.
export const relativeGamePath = (path, root) => {
	if (isJunk(path) || path.endsWith("/") || !path.startsWith(root)) return null;
	return path.slice(root.length) || null;
};

// SDK files games load from the Akedo CDN ("…/sdk/akedo_sdk_js.js"), also as
// JSON-escaped URLs ("https:\/\/…\/sdk\/…"). Offline they come from the game folder.
const SDK_FILES = ["akedo_sdk_js.js", "akedo_sdk.js", "logo.png"];
const SDK_URL_PATTERN = /https?:(?:\/\/|\\\/\\\/)[^"'\s)<>]*?(?:\/|\\\/)sdk(?:\/|\\\/)(akedo_sdk_js\.js|akedo_sdk\.js|logo\.png)(?:\?[^"'\s)<>]*)?/gi;

export const rewriteSdkUrls = (text, appId) =>
	text.replace(SDK_URL_PATTERN, (_url, fileName) => akedoFileUrl(appId, fileName.toLowerCase()));

export const OFFLINE_SDK_FILES = SDK_FILES.map((fileName) => ({ fileName, source: `/offline-sdk/${fileName}` }));

// Runs first in the game page: games must not register their own service worker
// (it would take over from Akedo's and can't see the downloaded files), and SDK
// URLs built at runtime are pointed at the local copies.
const bootstrapScript = (appId) => `<script data-akedo-offline>(function () {
	try {
		if (navigator.serviceWorker) {
			navigator.serviceWorker.register = function () {
				return Promise.reject(new Error("Service workers are disabled in Akedo offline games"));
			};
		}
	} catch (_) {}
	var local = ${JSON.stringify(Object.fromEntries(SDK_FILES.map((fileName) => [fileName, akedoFileUrl(appId, fileName)])))};
	function rewrite(value) {
		var match = /\\/sdk\\/(akedo_sdk_js\\.js|akedo_sdk\\.js|logo\\.png)(\\?.*)?$/i.exec(String(value || ""));
		return match ? local[match[1].toLowerCase()] : value;
	}
	var setAttribute = Element.prototype.setAttribute;
	Element.prototype.setAttribute = function (name, value) {
		var key = String(name || "").toLowerCase();
		return setAttribute.call(this, name, key === "src" || key === "href" ? rewrite(value) : value);
	};
	function rewriteNode(node) {
		if (node && node.tagName === "SCRIPT" && node.src) node.src = rewrite(node.src);
		return node;
	}
	var appendChild = Node.prototype.appendChild;
	Node.prototype.appendChild = function (node) { return appendChild.call(this, rewriteNode(node)); };
	var insertBefore = Node.prototype.insertBefore;
	Node.prototype.insertBefore = function (node, ref) { return insertBefore.call(this, rewriteNode(node), ref); };
})();</script>`;

export const patchIndexHtml = (html, appId) => {
	if (html.includes("data-akedo-offline")) return html;
	const script = bootstrapScript(appId);
	const headMatch = /<head[^>]*>/i.exec(html);
	if (headMatch) return html.slice(0, headMatch.index + headMatch[0].length) + script + html.slice(headMatch.index + headMatch[0].length);
	return script + html;
};

export const patchTextFile = (path, text, appId) => {
	const rewritten = rewriteSdkUrls(text, appId);
	return /(^|\/)index\.html$/i.test(path) ? patchIndexHtml(rewritten, appId) : rewritten;
};

// The user id from the site's JWT ("token" in localStorage). Download keys are
// encrypted with it; anonymous visitors without a token use "".
export const userIdFromToken = (token) => {
	try {
		const payload = String(token || "").split(".")[1];
		if (!payload) return "";
		const json = JSON.parse(decodeURIComponent(escape(atob(payload.replace(/-/g, "+").replace(/_/g, "/")))));
		return json?.id ? String(json.id) : "";
	} catch (_) {
		return "";
	}
};
